import {
  ClaimsSearchAction,
  ClaimsSearchActionTypes,
} from './claims-search.actions';

import {
  ClaimsSearchCriteria,
  ClaimsReferSearchCriteria,
  ClaimsSearchResponse,
  ClaimDetails,
  RemittanceDetailsResponse,
  Member,
  RemittanceReadResponse,
  ClaimTab,
  ClaimTabType,
  ServiceLineInfo,
  Adjustment,
  SUBMIT_STAGE,
} from '@cigna/chcp/claims/shared/util';

import { ProviderByTin } from '@cigna/chcp/shared/provider-data-access';
import {
  ServiceError,
  Attachment,
  FileWithUrl,
  SupportingInformation,
  metaToAttachment,
  AttachmentMetaData,
} from '@cigna/chcp/shared/util';

export const CLAIMSSEARCH_FEATURE_KEY = 'claimsSearch';

export interface ClaimsSearchState {
  providerLoadingState: boolean;
  providers: ProviderByTin[];
  startAReconsiderationFormData: Partial<Adjustment> | null;
  searchCriteria: ClaimsSearchCriteria | null;
  searchClaimCriteria: ClaimsReferSearchCriteria | null;
  multiplePatientsCriteria: ClaimsSearchCriteria | null;
  selectedTab: ClaimTabType;
  searchStage: SUBMIT_STAGE;
  results: ClaimsSearchResponse | null;
  claimDetails: ClaimDetails | null;
  memberLists: Member[] | null;
  isMultiplePatients: boolean;
  remittanceDetailsResponse: RemittanceDetailsResponse | null;
  multiplePatientUniqueIdentifier: string;
  markAsReadResponse: RemittanceReadResponse | null;
  error?: ServiceError | null;
  providerLoadingError: ServiceError | null;
  isReconsiderationHistoryOpened: boolean;
  isSupportingInformationTabOpened: boolean;
  historyDataIndex: number | null;
  adjustments: Adjustment[];
  adjustmentsLoading: boolean;
  adjustmentsError?: ServiceError;
  supportingInformationHistory: SupportingInformation[];
  supportingInformationHistoryLoading: boolean;
  supportingInformationHistoryError?: ServiceError;
  supportingInformationUploaded: Attachment[];
  supportingInformationSubmssionId?: string;
  supportingInformationSubmitLoading: boolean;
  supportingInformationSubmitError?: ServiceError;
  supportingInformationUploadedRemoveError?: ServiceError;
  selectedServiceLine: ServiceLineInfo[];
}

export interface ClaimsSearchPartialState {
  readonly [CLAIMSSEARCH_FEATURE_KEY]: ClaimsSearchState;
}

export const initialState: ClaimsSearchState = {
  providerLoadingState: false,
  providerLoadingError: null,
  providers: [],
  startAReconsiderationFormData: null,
  adjustments: [],
  searchCriteria: null,
  searchClaimCriteria: null,
  multiplePatientsCriteria: null,
  selectedTab: ClaimTab.MEMBER,
  searchStage: SUBMIT_STAGE.default,
  results: null,
  claimDetails: null,
  memberLists: null,
  isMultiplePatients: false,
  remittanceDetailsResponse: null,
  multiplePatientUniqueIdentifier: '',
  error: null,
  markAsReadResponse: null,
  isReconsiderationHistoryOpened: false,
  isSupportingInformationTabOpened: false,
  historyDataIndex: null,
  adjustmentsLoading: false,
  supportingInformationHistory: [],
  supportingInformationHistoryLoading: false,
  supportingInformationUploaded: [],
  supportingInformationSubmitLoading: false,
  selectedServiceLine: [],
};

const selectedValues = (state: any, payload: any) => {
  const summaryData: any = {
    ...state.remittanceDetailsResponse.remittanceSummaryData,
    remittanceData: state.remittanceDetailsResponse.remittanceSummaryData.remittanceData.map(
      (val: any) => ({
        ...val,
        readRemittance:
          val.remittanceTrackingNumber ===
          payload.remittanceReadData.remittance[0].Num
            ? !val.readRemittance
            : val.readRemittance,
      }),
    ),
  };
  return {
    ...state.remittanceDetailsResponse,
    remittanceSummaryData: summaryData,
  };
};

const addAttachmentsToSupInfo = (
  supInfo: SupportingInformation,
  payload: { id: string; attachments: AttachmentMetaData[] },
): SupportingInformation =>
  supInfo.id === payload.id
    ? {
        ...supInfo,
        attachmentsData: payload.attachments.map(metaToAttachment),
      }
    : supInfo;

const sortByDate = <T extends Record<string, any>>(
  arr: T[],
  key: keyof T,
): T[] =>
  [...arr].sort(
    (a, b) =>
      new Date(b[key] || '').getTime() - new Date(a[key] || '').getTime(),
  );
export function claimsSearchReducer(
  state: ClaimsSearchState = initialState,
  action: ClaimsSearchAction,
): ClaimsSearchState {
  // TODO: resolve CHCP disables by bot
  // eslint-disable-next-line sonarjs/max-switch-cases
  switch (action.type) {
    case ClaimsSearchActionTypes.NewSearch:
    case ClaimsSearchActionTypes.ResetSearch: {
      return initialState;
    }
    case ClaimsSearchActionTypes.ResetSearchStage: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.default,
      };
    }
    case ClaimsSearchActionTypes.BackToMultiplePatients:
    case ClaimsSearchActionTypes.ModifySearch: {
      return {
        ...state,
        results: null,
        multiplePatientUniqueIdentifier: '',
        error: null,
      };
    }

    case ClaimsSearchActionTypes.SearchClaims: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.submitting,
        searchCriteria: action.payload,
        searchClaimCriteria: null,
        memberLists: null,
        multiplePatientUniqueIdentifier: '',
        error: null,
      };
    }

    case ClaimsSearchActionTypes.ClaimsNumberSearch: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.submitting,
        searchClaimCriteria: action.payload,
        searchCriteria: null,
        memberLists: null,
        error: null,
      };
    }

    case ClaimsSearchActionTypes.SearchClaimsSuccess:
    case ClaimsSearchActionTypes.ClaimsNumberSearchSuccess: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.default,
        results: action.payload,
        memberLists: action.payload.memberList.map(
          (x: Member, index: number) => {
            const member = { ...x };
            member.uniqueMemberKey = `${member.memberId}_${index}`;
            return member;
          },
        ),
        isMultiplePatients:
          action.payload.memberList && action.payload.memberList.length > 1,
        error: null,
      };
    }

    case ClaimsSearchActionTypes.MultiplePatients: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.submitting,
        multiplePatientUniqueIdentifier: action.payload.uniqueMemberKey
          ? action.payload.uniqueMemberKey
          : '',
        multiplePatientsCriteria: action.payload,
        error: null,
      };
    }

    case ClaimsSearchActionTypes.MultiplePatientsSuccess: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.success,
        results: action.payload,
        multiplePatientUniqueIdentifier: '',
        error: null,
      };
    }

    case ClaimsSearchActionTypes.TabSelection: {
      return {
        ...state,
        selectedTab: action.payload,
      };
    }

    case ClaimsSearchActionTypes.SearchClaimsError:
    case ClaimsSearchActionTypes.ClaimsNumberSearchError: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.error,
        error: action.payload,
        results: null,
      };
    }

    case ClaimsSearchActionTypes.DisplayClaimsDetails: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.submitting,
        claimDetails: null,
        error: null,
        providers: [],
        historyDataIndex: null,
        selectedServiceLine: [],
      };
    }

    case ClaimsSearchActionTypes.LoadClaimsDetailsSuccess: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.default,
        claimDetails: action.payload,
        error: null,
      };
    }

    case ClaimsSearchActionTypes.LoadClaimsDDARSuccess: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.default,
        claimDetails: action.payload.claimDetailsResponse,
        remittanceDetailsResponse: action.payload.remittanceDetailsResponse,
        error: null,
      };
    }

    case ClaimsSearchActionTypes.LoadClaimsDetailsError: {
      return {
        ...state,
        searchStage: SUBMIT_STAGE.error,
        claimDetails: null,
        remittanceDetailsResponse: null,
        error: action.payload,
      };
    }

    case ClaimsSearchActionTypes.RemittanceReadStatusSuccess: {
      return {
        ...state,
        markAsReadResponse: action.payload,
        remittanceDetailsResponse: selectedValues(state, action.payload),
        error: null,
      };
    }

    case ClaimsSearchActionTypes.RemittanceReadStatusError: {
      return {
        ...state,
        error: action.payload,
      };
    }

    case ClaimsSearchActionTypes.FetchAdjustments: {
      return {
        ...state,
        adjustmentsError: undefined,
        adjustmentsLoading: true,
      };
    }

    case ClaimsSearchActionTypes.FetchAdjustmentsSuccess: {
      return {
        ...state,
        adjustments: action.payload,
        adjustmentsError: undefined,
        adjustmentsLoading: false,
      };
    }

    case ClaimsSearchActionTypes.FetchAdjustmentsError: {
      return {
        ...state,
        adjustmentsLoading: false,
        adjustmentsError: action.payload,
      };
    }

    case ClaimsSearchActionTypes.RemoveAdjustmentSuccess: {
      return {
        ...state,
        adjustments: state.adjustments.filter(
          (v) => v.id !== action.payload.id,
        ),
        adjustmentsError: undefined,
        adjustmentsLoading: false,
      };
    }

    case ClaimsSearchActionTypes.SetIsReconsiderationHistoryOpened: {
      return {
        ...state,
        isReconsiderationHistoryOpened: action.payload,
      };
    }
    case ClaimsSearchActionTypes.ResetReconsiderationInfo: {
      return {
        ...state,
        historyDataIndex: null,
        startAReconsiderationFormData: null,
      };
    }

    case ClaimsSearchActionTypes.SetSupportingInformationTabOpened: {
      return {
        ...state,
        isSupportingInformationTabOpened: action.payload,
      };
    }
    case ClaimsSearchActionTypes.SetHistoryDataIndex: {
      return {
        ...state,
        historyDataIndex: action.payload,
      };
    }
    case ClaimsSearchActionTypes.GetProviderByTin: {
      return {
        ...state,
        providerLoadingState: true,
        providerLoadingError: null,
      };
    }
    case ClaimsSearchActionTypes.GetProviderByTinSuccess: {
      return {
        ...state,
        providers: action.payload
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name)),
        providerLoadingState: false,
      };
    }
    case ClaimsSearchActionTypes.GetProviderByTinError: {
      return {
        ...state,
        providerLoadingError: action.error,
        providerLoadingState: true,
      };
    }
    case ClaimsSearchActionTypes.SaveStartAReconsiderationData: {
      return {
        ...state,
        startAReconsiderationFormData: action.payload,
      };
    }
    case ClaimsSearchActionTypes.GetSupportingInformationHistory: {
      return {
        ...state,
        supportingInformationHistory: [],
        supportingInformationHistoryLoading: true,
        supportingInformationHistoryError: undefined,
      };
    }
    case ClaimsSearchActionTypes.GetSupportingInformationHistorySuccess: {
      return {
        ...state,
        supportingInformationHistory: sortByDate(
          action.payload,
          'submittedDate',
        ),
        supportingInformationHistoryLoading: false,
      };
    }
    case ClaimsSearchActionTypes.GetSupportingInformationHistoryFailed: {
      return {
        ...state,
        supportingInformationHistoryLoading: false,
        supportingInformationHistoryError: action.error,
      };
    }
    case ClaimsSearchActionTypes.GetSupportingInformationAttachmentsSuccess: {
      return {
        ...state,
        supportingInformationHistory: state.supportingInformationHistory.map(
          (v) => addAttachmentsToSupInfo(v, action.payload),
        ),
      };
    }
    case ClaimsSearchActionTypes.UploadAttachments: {
      return {
        ...state,
        supportingInformationUploaded: [
          ...state.supportingInformationUploaded.filter(
            (attachmnent) =>
              !action.payload.some(
                (fileWithUrl) => attachmnent.name === fileWithUrl.file.name,
              ),
          ),
          ...action.payload.map(
            (v: FileWithUrl) =>
              (({
                ...v,
                name: v.file.name,
                isLoading: true,
              } as unknown) as Attachment),
          ),
        ],
      };
    }
    case ClaimsSearchActionTypes.GetAttachmentDetailsForUploadedSuccess: {
      return {
        ...state,
        supportingInformationUploaded: state.supportingInformationUploaded.map(
          (v: Attachment): Attachment =>
            v.name === action.payload.name
              ? { ...v, ...action.payload, isLoading: false }
              : v,
        ),
      };
    }
    case ClaimsSearchActionTypes.FileToS3UploadProgress: {
      return {
        ...state,
        supportingInformationUploaded: state.supportingInformationUploaded.map(
          (v: Attachment): Attachment =>
            v.name === action.payload.file.name
              ? { ...v, loaded: action.payload.loaded }
              : v,
        ),
      };
    }
    case ClaimsSearchActionTypes.GetAttachmentsSignedUrlToPutFileFailed:
    case ClaimsSearchActionTypes.PutFileToS3Failed:
    case ClaimsSearchActionTypes.GetAttachmentDetailsForUploadedFailed: {
      return {
        ...state,
        supportingInformationUploaded: state.supportingInformationUploaded.map(
          (v: Attachment): Attachment =>
            v.file?.name && action.payload.fileNames.includes(v.file.name)
              ? { ...v, error: 'SERVER_ERROR', isLoading: false }
              : v,
        ),
      };
    }

    case ClaimsSearchActionTypes.SetSupportingInfoSubmissionNumber:
      return {
        ...state,
        supportingInformationSubmssionId: action.payload,
      };

    case ClaimsSearchActionTypes.SubmitSupportingInformation:
      return {
        ...state,
        supportingInformationSubmitLoading: true,
        supportingInformationSubmitError: undefined,
      };

    case ClaimsSearchActionTypes.SubmitSupportingInformationSuccess:
      return {
        ...state,
        supportingInformationUploaded: [],
        supportingInformationHistory: [
          action.payload,
          ...state.supportingInformationHistory,
        ],
        supportingInformationSubmitLoading: false,
        supportingInformationSubmssionId: undefined,
      };

    case ClaimsSearchActionTypes.SubmitSupportingInformationFailed:
      return {
        ...state,
        supportingInformationSubmitLoading: false,
        supportingInformationSubmitError: action.error,
      };

    case ClaimsSearchActionTypes.RemoveAttachment:
      return {
        ...state,
        supportingInformationUploadedRemoveError: undefined,
        supportingInformationUploaded: state.supportingInformationUploaded.filter(
          (v: Attachment) => v.name !== action.payload,
        ),
      };

    case ClaimsSearchActionTypes.RemoveAttachmentFailed:
      return {
        ...state,
        supportingInformationUploadedRemoveError: action.error,
      };

    case ClaimsSearchActionTypes.SetSelectedServiceLine:
      return {
        ...state,
        selectedServiceLine: [...state.selectedServiceLine, action.payload],
      };

    case ClaimsSearchActionTypes.RemoveSelectedServiceLine:
      return {
        ...state,
        selectedServiceLine: [
          ...state.selectedServiceLine.filter((v) => v.id !== action.payload),
        ],
      };

    default: {
      return state;
    }
  }
}
